/**
 * The queue's sheet lives in the URL, so every open ticket is a link:
 *
 *   ?item=<id>   the ticket sheet (a UUID or a human identifier)
 *   ?add=1       the Add tickets sheet (`&q=` opens it with that search typed)
 *   ?sees=1      What Bailey sees (the queue header's source line)
 *
 * Opening a row pushes `?item=`; moving to the next ticket replaces it; the
 * sheet's Close (or Escape) drops it. The sheet itself is the shell's one
 * sheet (`useSheet`): this hook puts the ticket's content in it and keeps the
 * two in step, both ways.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import type { WorkItem } from "@yagni/shared";
import { sheetStore } from "../shell/useSheet.js";
import { TicketSheet } from "./TicketSheet.js";
import { pipelineRowFor } from "../work/workViews.js";
import { captureUiError } from "../sentry.js";

/** Read one ticket the loaded list does not carry (a deep link to an archived or older one). */
export function useWorkItemById(
  id: string | null,
  enabled: boolean,
  /** A change here reads the item again. */
  refreshKey = "",
): WorkItem | null {
  const [item, setItem] = useState<WorkItem | null>(null);
  useEffect(() => {
    if (!enabled || !id) {
      setItem(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(id)}`, { credentials: "include" })
      .then((res) => {
        // A ticket the reader cannot see is a 404, and the sheet stays shut.
        if (res.status === 404) return null;
        if (!res.ok) throw new Error(`ticket read failed (${res.status})`);
        return res.json() as Promise<WorkItem>;
      })
      .then((body) => {
        if (!cancelled) setItem(body && typeof body.id === "string" ? body : null);
      })
      .catch((err: unknown) => {
        captureUiError(err, { area: "Work", operation: "read_sheet_ticket", workItemId: id });
        if (!cancelled) setItem(null);
      });
    return () => {
      cancelled = true;
    };
  }, [id, enabled, refreshKey]);
  return item;
}

export interface QueueSheetOptions {
  /** Every ticket the surface has loaded. */
  items: readonly WorkItem[];
  /** The next ticket that needs the reader after this one, in the queue's order. */
  nextAfter: (id: string) => string | null;
  /** The Add tickets sheet, when the surface offers one. */
  renderAdd?: (close: () => void, openItem: (id: string) => void) => ReactNode;
  /** What Bailey sees, when the surface offers it. */
  renderSees?: (close: () => void, openItem: (id: string) => void) => ReactNode;
}

export interface QueueSheet {
  /** The ticket open in the sheet, by its row id. */
  selectedId: string | null;
  addOpen: boolean;
  seesOpen: boolean;
  openItem: (id: string, options?: { replace?: boolean }) => void;
  openAdd: () => void;
  openSees: () => void;
  close: () => void;
}

export function useQueueSheet({
  items,
  nextAfter,
  renderAdd,
  renderSees,
}: QueueSheetOptions): QueueSheet {
  const [params, setParams] = useSearchParams();
  const itemParam = params.get("item");
  const addOpen = params.get("add") === "1" && renderAdd != null;
  const seesOpen = !addOpen && params.get("sees") === "1" && renderSees != null;

  const listed = itemParam
    ? (items.find(
        (entry) =>
          entry.id === itemParam || entry.identifier.toLowerCase() === itemParam.toLowerCase(),
      ) ?? null)
    : null;
  const fetched = useWorkItemById(itemParam, itemParam != null && listed == null);
  const item =
    listed ??
    (fetched && (fetched.id === itemParam || fetched.identifier === itemParam) ? fetched : null);

  const openItem = useCallback(
    (id: string, options?: { replace?: boolean }) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("item", id);
          next.delete("add");
          next.delete("q");
          next.delete("sees");
          return next;
        },
        options?.replace ? { replace: true } : undefined,
      );
    },
    [setParams],
  );
  const openAdd = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("add", "1");
      next.delete("q");
      next.delete("item");
      next.delete("sees");
      return next;
    });
  }, [setParams]);
  const openSees = useCallback(() => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("sees", "1");
      next.delete("item");
      next.delete("add");
      next.delete("q");
      return next;
    });
  }, [setParams]);
  const close = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("item");
        next.delete("add");
        next.delete("q");
        next.delete("sees");
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  // What this hook last put in the sheet, so a close the reader made (Escape,
  // Close) can drop the param, and a sheet another surface opened is left be.
  const ours = useRef<ReactNode | null>(null);
  const latest = useRef({ nextAfter, renderAdd, renderSees, openItem, close });
  latest.current = { nextAfter, renderAdd, renderSees, openItem, close };

  useEffect(() => {
    let content: ReactNode | null = null;
    if (addOpen) {
      content =
        latest.current.renderAdd?.(latest.current.close, (id) =>
          latest.current.openItem(id, { replace: true }),
        ) ?? null;
    } else if (seesOpen) {
      content =
        latest.current.renderSees?.(latest.current.close, (id) => latest.current.openItem(id)) ??
        null;
    } else if (item) {
      content = (
        <TicketSheet
          item={item}
          // The row the board prints for this ticket, facets folded in, so the
          // drawer speaks for the same item the row does.
          row={pipelineRowFor(item, items)}
          nextItemId={latest.current.nextAfter(item.id)}
          onOpenItem={(id) => latest.current.openItem(id, { replace: true })}
          onClose={() => latest.current.close()}
        />
      );
    }
    if (content) {
      ours.current = content;
      sheetStore.open(content, {
        wide: sheetStore.getSnapshot().content != null && sheetStore.getSnapshot().wide,
      });
      return;
    }
    if (
      !itemParam &&
      !addOpen &&
      !seesOpen &&
      ours.current &&
      sheetStore.getSnapshot().content === ours.current
    ) {
      ours.current = null;
      sheetStore.close();
    }
  }, [item, addOpen, seesOpen, itemParam, items]);

  // The reader closed the sheet: the URL follows. A route change closes the
  // sheet too, and then this surface is on its way out and the next page's
  // `?item=` is not its to drop, so the close waits a tick and asks first.
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(
    () =>
      sheetStore.subscribe(() => {
        if (sheetStore.getSnapshot().content != null || ours.current == null) return;
        ours.current = null;
        setTimeout(() => {
          if (alive.current) latest.current.close();
        }, 0);
      }),
    [],
  );

  // Leaving the surface takes its sheet with it.
  useEffect(
    () => () => {
      if (ours.current && sheetStore.getSnapshot().content === ours.current) {
        ours.current = null;
        sheetStore.close();
      }
    },
    [],
  );

  return { selectedId: item?.id ?? null, addOpen, seesOpen, openItem, openAdd, openSees, close };
}
